// ==============================
// EditAdvertisingBannerDialog.jsx
// ==============================
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Switch,
  FormControlLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from "@mui/material";

import { updateAdvertisingBanner } from "../../../services/Admin/AdvertisingBannersApi";
import { getAdminCategories } from "../../../services/Admin/ProductsAndCategoriesApi";

const EditAdvertisingBannerDialog = ({ open, onClose, banner, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  // ✅ ملفات جديدة (اختياري)
  const [imageFile, setImageFile] = useState(null);
  const [imageWebFile, setImageWebFile] = useState(null);

  // ✅ بيانات البانر
  const [titleAr, setTitleAr] = useState("");
  const [titleEn, setTitleEn] = useState("");
  const [descAr, setDescAr] = useState("");
  const [descEn, setDescEn] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [displayOrder, setDisplayOrder] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [categoryIds, setCategoryIds] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setError("");
      setImageFile(null);
      setImageWebFile(null);

      // ✅ تعبئة الحقول من البانر الحالي
      setTitleAr(banner?.title_ar || "");
      setTitleEn(banner?.title_en || "");
      setDescAr(banner?.description_ar || "");
      setDescEn(banner?.description_en || "");
      setIsActive(!!banner?.is_active);
      setDisplayOrder(banner?.display_order ?? "");
      setStartDate(banner?.start_date || "");
      setEndDate(banner?.end_date || "");

      // ✅ التصنيفات (حسب شكل بياناتك)
      // لو البانر عنده category_ids مباشرة
      if (Array.isArray(banner?.category_ids)) {
        setCategoryIds(banner.category_ids.map((x) => Number(x)));
      } else if (Array.isArray(banner?.categories)) {
        // لو جاي categories objects [{id:..}]
        setCategoryIds(banner.categories.map((x) => Number(x.id)));
      } else {
        setCategoryIds([]);
      }

      fetchCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, banner]);

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const res = await getAdminCategories();
      const data = res?.success ? res.data : res;
      setCategories(Array.isArray(data) ? data : []);
    } catch {
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!banner?.id) return;

    setError("");
    setLoading(true);

    try {
      // ✅ الصورة مش إجبار:
      // لو مفيش ملف جديد -> نبعت القديمة (banner.image / banner.image_web) كنص
      const payload = {
        image: imageFile, // File أو null
        image_web: imageWebFile, // File أو null

        title_ar: titleAr.trim(),
        title_en: titleEn.trim(),
        description_ar: descAr.trim(),
        description_en: descEn.trim(),

        is_active: !!isActive,
        display_order: displayOrder === "" ? "" : Number(displayOrder),

        start_date: startDate || "",
        end_date: endDate || "",

        // لو التحديث يدعم التصنيفات
        category_ids: Array.isArray(categoryIds) ? categoryIds.map(Number) : [],
      };

      const res = await updateAdvertisingBanner(banner.id, payload);

      // ✅ لأن API بيرجع message_* غالبًا بدون success
      if (res?.success || res?.message_en || res?.message_ar) {
        onSuccess?.();
      } else {
        setError("فشل تحديث البانر");
      }
    } catch (err) {
      setError(err?.message || "حدث خطأ أثناء التحديث");
    } finally {
      setLoading(false);
    }
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 280,
      },
    },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>تعديل بانر إعلاني</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="caption">
              تغيير صورة الموبايل (اختياري)
            </Typography>
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                setImageFile(
                  e.target.files && e.target.files[0]
                    ? e.target.files[0]
                    : null,
                )
              }
              style={{ display: "block", marginTop: 8 }}
            />
            <Typography variant="caption" color="text.secondary">
              {imageFile
                ? `ملف جديد: ${imageFile.name}`
                : `الحالي: ${banner?.image || "-"}`}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption">
              تغيير صورة الويب (اختياري)
            </Typography>
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                setImageWebFile(
                  e.target.files && e.target.files[0]
                    ? e.target.files[0]
                    : null,
                )
              }
              style={{ display: "block", marginTop: 8 }}
            />
            <Typography variant="caption" color="text.secondary">
              {imageWebFile
                ? `ملف جديد: ${imageWebFile.name}`
                : `الحالي: ${banner?.image_web || "-"}`}
            </Typography>
          </Box>

          <TextField
            label="العنوان (عربي)"
            value={titleAr}
            onChange={(e) => setTitleAr(e.target.value)}
            fullWidth
          />
          <TextField
            label="العنوان (إنجليزي)"
            value={titleEn}
            onChange={(e) => setTitleEn(e.target.value)}
            fullWidth
          />
          <TextField
            label="الوصف (عربي)"
            value={descAr}
            onChange={(e) => setDescAr(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
          <TextField
            label="الوصف (إنجليزي)"
            value={descEn}
            onChange={(e) => setDescEn(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />

          <FormControl fullWidth size="small">
            <InputLabel>التصنيفات</InputLabel>
            <Select
              multiple
              value={categoryIds}
              onChange={(e) =>
                setCategoryIds(
                  typeof e.target.value === "string"
                    ? e.target.value.split(",")
                    : e.target.value,
                )
              }
              input={<OutlinedInput label="التصنيفات" />}
              renderValue={(selected) =>
                (selected || [])
                  .map((id) => {
                    const c = categories.find(
                      (x) => Number(x.id) === Number(id),
                    );
                    return c ? c.name_ar || c.name_en : id;
                  })
                  .join(", ")
              }
              MenuProps={MenuProps}
            >
              {categoriesLoading ? (
                <MenuItem disabled>جارٍ التحميل...</MenuItem>
              ) : (
                categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    <Checkbox
                      checked={categoryIds.some(
                        (x) => Number(x) === Number(c.id),
                      )}
                    />
                    <ListItemText primary={c.name_ar || c.name_en} />
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label={isActive ? "مفعّل" : "معطّل"}
            />
            <TextField
              label="الترتيب"
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
              size="small"
              sx={{ width: 160 }}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="تاريخ البدء"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="تاريخ الانتهاء"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>

          {error && <Typography color="error">{error}</Typography>}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          إلغاء
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={18} /> : "حفظ"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAdvertisingBannerDialog;
